import { useCallback, useMemo } from 'react';

import type { OnboardingModalName, OnboardingModalSource } from 'src/utils/analytics/events';
import { tracker } from 'src/utils/analytics';
import { getLogger } from 'src/utils/datadog-logger';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';

const LOG_NAMESPACE = 'post-signup-onboarding';

type LogFn = (message: string, context?: Record<string, unknown>) => void;

type TrackErroredArgs = {
  errorCode?: string;
  errorMessage?: string;
};

export type OnboardingModalContext = {
  source?: OnboardingModalSource;
  flowPosition?: number;
};

export type UseOnboardingModalAnalyticsReturn = {
  trackOpened: () => void;
  trackCompleted: () => void;
  trackDeclined: () => void;
  trackDismissed: () => void;
  trackErrored: (args: TrackErroredArgs) => void;

  logError: LogFn;
  logWarn: LogFn;
  logInfo: LogFn;
};

type LoggerHelpers = {
  logError: LogFn;
  logWarn: LogFn;
  logInfo: LogFn;
};

// Shared by both onboarding analytics hooks so `modal`/`source` context is attached
// to log lines identically regardless of whether the caller is a modal or the flow sequencer.
function useOnboardingLoggerHelpers(logContext: {
  modal?: OnboardingModalName;
  source?: OnboardingModalSource;
}): LoggerHelpers {
  const { modal, source } = logContext;
  const logger = useMemo(() => getLogger(LOG_NAMESPACE), []);

  const logError = useCallback(
    (message: string, additionalContext?: Record<string, unknown>): void => {
      logger.error(message, { modal, source, ...additionalContext });
    },
    [logger, modal, source]
  );

  const logWarn = useCallback(
    (message: string, additionalContext?: Record<string, unknown>): void => {
      logger.warn(message, { modal, source, ...additionalContext });
    },
    [logger, modal, source]
  );

  const logInfo = useCallback(
    (message: string, additionalContext?: Record<string, unknown>): void => {
      logger.info(message, { modal, source, ...additionalContext });
    },
    [logger, modal, source]
  );

  return { logError, logWarn, logInfo };
}

// Modal identity and context are merged into every event and log line; callers pass neither.
export const useOnboardingModalAnalytics = (
  modal: OnboardingModalName,
  context?: OnboardingModalContext
): UseOnboardingModalAnalyticsReturn => {
  const { source, flowPosition } = context ?? {};
  const { eventToken } = useDutchiePayAnalytics();

  const trackOpened = useCallback((): void => {
    tracker.onboardingModalStep({ modal, action: 'opened', source, dutchiePaySessionToken: eventToken, flowPosition });
  }, [modal, source, eventToken, flowPosition]);

  const trackCompleted = useCallback((): void => {
    tracker.onboardingModalStep({
      modal,
      action: 'completed',
      source,
      dutchiePaySessionToken: eventToken,
      flowPosition,
    });
  }, [modal, source, eventToken, flowPosition]);

  const trackDeclined = useCallback((): void => {
    tracker.onboardingModalStep({
      modal,
      action: 'declined',
      source,
      dutchiePaySessionToken: eventToken,
      flowPosition,
    });
  }, [modal, source, eventToken, flowPosition]);

  const trackDismissed = useCallback((): void => {
    tracker.onboardingModalStep({
      modal,
      action: 'dismissed',
      source,
      dutchiePaySessionToken: eventToken,
      flowPosition,
    });
  }, [modal, source, eventToken, flowPosition]);

  const trackErrored = useCallback(
    ({ errorCode, errorMessage }: TrackErroredArgs): void => {
      tracker.onboardingModalStep({
        modal,
        action: 'errored',
        source,
        dutchiePaySessionToken: eventToken,
        flowPosition,
        errorCode,
        errorMessage,
      });
    },
    [modal, source, eventToken, flowPosition]
  );

  const { logError, logWarn, logInfo } = useOnboardingLoggerHelpers({ modal, source });

  return {
    trackOpened,
    trackCompleted,
    trackDeclined,
    trackDismissed,
    trackErrored,
    logError,
    logWarn,
    logInfo,
  };
};

type TrackFlowStartedArgs = {
  eligibleSteps: OnboardingModalName[];
  conditionalSteps?: OnboardingModalName[];
};

export type UseOnboardingFlowAnalyticsReturn = {
  trackFlowStarted: (args: TrackFlowStartedArgs) => void;

  logError: LogFn;
  logWarn: LogFn;
  logInfo: LogFn;
};

// Emits the flow-level start event once eligibility for the post-signup sequence is known.
export const useOnboardingFlowAnalytics = ({
  source,
}: {
  source: OnboardingModalSource;
}): UseOnboardingFlowAnalyticsReturn => {
  const { eventToken } = useDutchiePayAnalytics();

  const trackFlowStarted = useCallback(
    ({ eligibleSteps, conditionalSteps }: TrackFlowStartedArgs): void => {
      tracker.onboardingFlowStarted({ source, dutchiePaySessionToken: eventToken, eligibleSteps, conditionalSteps });
    },
    [source, eventToken]
  );

  const { logError, logWarn, logInfo } = useOnboardingLoggerHelpers({ source });

  return {
    trackFlowStarted,
    logError,
    logWarn,
    logInfo,
  };
};
